"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.COALESCE_WINDOW_MINUTES = exports.ROUTINE_DAILY_CAP = void 0;
exports.isUrgent = isUrgent;
exports.decideDelivery = decideDelivery;
exports.inQuietHours = inQuietHours;
exports.coalesceBucket = coalesceBucket;
/**
 * Who gets told what, and when.
 *
 * Alert fatigue is the failure mode that kills this product. Six emails a day and Rob
 * and Karla build a Gmail filter — and then the one that matters is the one that gets
 * missed. Every rule below exists to keep the volume low enough that an alert still
 * means something.
 */
const time_js_1 = require("./time.js");
/** Roughly two routine emails a day. Past that, people stop reading them. */
exports.ROUTINE_DAILY_CAP = 2;
/** Non-urgent caregiver alerts are batched into buckets this many minutes wide. */
exports.COALESCE_WINDOW_MINUTES = 60;
/**
 * A red flag is never suppressed. Not by quiet hours, not by the daily cap, not by
 * coalescing. These are the symptoms the precautions document says to treat as a
 * possible emergency, and the entire alerting design exists so that this one arrives.
 */
function isUrgent(kind) {
    return kind === 'RED_FLAG_ALERT';
}
function decideDelivery(ctx) {
    if (isUrgent(ctx.kind)) {
        return { send: true, defer: false, reason: 'urgent: never suppressed' };
    }
    /*
     * A dose reminder goes to the patient at a time the patient's own schedule chose.
     * Quiet hours must NOT apply to it: the bedtime dose is at 21:30 and quiet hours
     * begin at 21:30, so honouring them here would silence the single reminder most
     * likely to be forgotten.
     */
    if (ctx.kind === 'DOSE_REMINDER' && ctx.recipient === 'PATIENT') {
        return { send: true, defer: false, reason: 'dose reminders follow the dose schedule' };
    }
    if (inQuietHours(ctx.now, ctx.tz, ctx.quietHours)) {
        return { send: false, defer: true, reason: 'quiet hours; rolled into the digest' };
    }
    if ((ctx.routineSentToday ?? 0) >= exports.ROUTINE_DAILY_CAP) {
        return { send: false, defer: true, reason: 'daily cap reached; rolled into the digest' };
    }
    return { send: true, defer: false, reason: 'routine' };
}
/**
 * Quiet hours, correctly handling the overnight case.
 *
 * 21:30–07:00 wraps midnight, so a naive `start <= now < end` comparison would report
 * that nothing is ever quiet.
 */
function inQuietHours(now, tz, quiet) {
    if (!quiet)
        return false;
    const minutes = (0, time_js_1.localHourIn)(now, tz) * 60 + minuteIn(now, tz);
    const start = toMinutes(quiet.start);
    const end = toMinutes(quiet.end);
    if (start === end)
        return false;
    return start < end ? minutes >= start && minutes < end : minutes >= start || minutes < end;
}
/**
 * The bucket a routine alert belongs to.
 *
 * Two missed doses forty minutes apart become one email rather than two, which is the
 * difference between an alert that gets read and a filter rule.
 */
function coalesceBucket(now, windowMinutes = exports.COALESCE_WINDOW_MINUTES) {
    const bucket = Math.floor(now.getTime() / (windowMinutes * 60_000));
    return String(bucket);
}
function toMinutes(hhmm) {
    const [h, m] = hhmm.split(':').map(Number);
    return (h ?? 0) * 60 + (m ?? 0);
}
function minuteIn(instant, tz) {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: tz,
        minute: '2-digit',
        hour12: false,
    }).formatToParts(instant);
    return Number(parts.find((p) => p.type === 'minute')?.value ?? '0');
}
//# sourceMappingURL=notifications.js.map